import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { IntlShape } from 'react-intl/src/types';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { HashMap } from 'shared/types/commonView';
import { GridColumnField, GridColumnFieldIssue, GridColumnShape } from './types';
import { Typography } from '@mui/material';
import { ELLIPSIS_TEXT_SX } from 'components/common/dataGrid/styles';
import {
  COLUMN_SHAPES,
  getCreatedAtGridColumnShape,
  getCreatedByGridColumnShape,
  getSiteGridColumnShape,
  getModifiedAtGridColumnShape,
  getModifiedByGridColumnShape,
  getLevelGridColumnShape,
  getDocumentStateGridColumnShape,
  getIssueStateGridColumnShape,
  getProcessGridColumnShape,
  getInspectionStatusGridColumnShape,
  getExecutionDateGridColumnShape,
  getInspectionAuthorGridColumnShape,
  getContractPartiesGridColumnShape,
  getUserRoleGridColumnShape,
  getUserSitesGridColumnShape,
  getUserProcessesGridColumnShape,
  getProjectInvolvementTypeGridColumnShape,
  getCompanySitesGridColumnShape,
  getCorrespondenceAddressCountryGridColumnShape,
  getRegisteredAddressCountryGridColumnShape,
} from './columnShapes';
import { ISO6391LanguageCode } from 'shared/types/locale';
import { isNil } from 'helpers/misc';

export abstract class DataGridColumnsFactory<T extends GridValidRowModel> {
  public intl: IntlShape;
  public timezone: string;

  constructor(intl: IntlShape, timezone: string) {
    this.timezone = timezone;
    this.intl = intl;
  }

  abstract create(): GridColDef<T>[];

  name(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.DOCUMENTATION.NAME,
      GridColumnField.name
    );
  }

  orderNumber(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.ORDER_NUMBER,
      GridColumnField.orderNumber
    );
  }

  number(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.DOCUMENTATION.NUMBER,
      GridColumnField.number
    );
  }

  currentVersion(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.DOCUMENTATION.CURRENT_VERSION,
      GridColumnField.currentVersion
    );
  }

  exportedFromIssue(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.DOCUMENTATION.EXPORTED_FROM_ISSUE,
      GridColumnField.exportedFromIssue
    );
  }

  createdAt(intl: IntlShape, timezone: string): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getCreatedAtGridColumnShape(intl, timezone),
      GridColumnField.createdAt
    );
  }

  modifiedAt(intl: IntlShape, timezone: string): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getModifiedAtGridColumnShape(intl, timezone),
      GridColumnField.modifiedAt
    );
  }

  createdBy(organizationId: string, intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getCreatedByGridColumnShape(organizationId, intl),
      GridColumnField.createdBy
    );
  }

  author(organizationId: string, intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getInspectionAuthorGridColumnShape(organizationId, intl),
      GridColumnField.author
    );
  }

  modifiedBy(organizationId: string, intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getModifiedByGridColumnShape(organizationId, intl),
      GridColumnField.modifiedBy
    );
  }

  relatedIssuesCount(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.DOCUMENTATION.RELATED_ISSUES_COUNT,
      GridColumnField.relatedIssuesCount
    );
  }

  parentDirectory(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.DOCUMENTATION.PARENT_DIRECTORY,
      GridColumnField.parentDirectory
    );
  }

  site(intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getSiteGridColumnShape(intl),
      GridColumnField.sites
    );
  }

  level(intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getLevelGridColumnShape(intl),
      GridColumnField.levels
    );
  }

  documentState(intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getDocumentStateGridColumnShape(intl),
      GridColumnField.state
    );
  }

  issueState(intl: IntlShape): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getIssueStateGridColumnShape(intl),
      GridColumnFieldIssue.state
    );
  }

  processCode(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.PROCESS_CODE,
      GridColumnField.processCode
    );
  }

  process(processes?: HashMap<any>): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getProcessGridColumnShape(this.intl, processes),
      GridColumnField.process
    );
  }

  inspectionNumber(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.INSPECTION_NUMBER,
      GridColumnField.inspectionNumber
    );
  }

  templateSummary(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.INSPECTION_SUMMARY,
      GridColumnField.summary
    );
  }

  inspectionStatus(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getInspectionStatusGridColumnShape(this.intl),
      GridColumnField.status
    );
  }

  executionDate(locale: ISO6391LanguageCode): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getExecutionDateGridColumnShape(locale, this.intl, this.timezone),
      GridColumnField.executionDate
    );
  }

  controlledParty(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.CONTROLLED_PARTY,
      GridColumnField.controlledParty
    );
  }

  workTypes(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.WORK_TYPES,
      GridColumnField.workTypes
    );
  }

  inspectionResult(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.INSPECTION.INSPECTION_RESULT,
      GridColumnField.inspectionResult
    );
  }

  contractLabel(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.CONTRACT.CONTRACT_LABEL,
      GridColumnField.label
    );
  }

  contractParties(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getContractPartiesGridColumnShape(this.intl),
      GridColumnField.parties
    );
  }

  userLabel(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.USER.USER_LABEL,
      GridColumnField.label
    );
  }

  userEmail(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.USER.USER_EMAIL,
      GridColumnField.email
    );
  }

  userRole(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getUserRoleGridColumnShape(this.intl),
      GridColumnField.role
    );
  }

  userPhone(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.USER.USER_PHONE,
      GridColumnField.phone
    );
  }

  userSites(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getUserSitesGridColumnShape(this.intl),
      GridColumnField.sites
    );
  }

  userProcesses(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getUserProcessesGridColumnShape(this.intl),
      GridColumnField.processes
    );
  }

  companyShortLabel(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.COMPANY_SHORT_LABEL,
      GridColumnField.shortLabel
    );
  }

  companyLongLabel(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.COMPANY_LONG_LABEL,
      GridColumnField.longLabel
    );
  }

  finalCostSettled(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.FINAL_COST_SETTLED,
      GridColumnField.finalCostSettled
    );
  }

  finalCostOutstanding(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.FINAL_COST_OUTSTANDING,
      GridColumnField.finalCostOutstanding
    );
  }

  estimatedCost(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.ESTIMATED_COST,
      GridColumnField.estimatedCost
    );
  }

  expectedFine(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.EXPECTED_FINE,
      GridColumnField.expectedFine
    );
  }

  imposedFine(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.IMPOSED_FINE,
      GridColumnField.imposedFine
    );
  }

  projectInvolvementType(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getProjectInvolvementTypeGridColumnShape(this.intl),
      GridColumnField.projectInvolvementType
    );
  }

  taxNumber(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.TAX_NUMBER,
      GridColumnField.taxNumber
    );
  }

  correspondenceAddressStreetAndNumber(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.CORRESPONDENCE_ADDRESS_STREET_AND_NUMBER,
      GridColumnField.correspondenceAddressStreetAndNumber
    );
  }

  correspondenceAddressPostalCode(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.CORRESPONDENCE_ADDRESS_POSTAL_CODE,
      GridColumnField.correspondenceAddressPostalCode
    );
  }

  correspondenceAddressCountry(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getCorrespondenceAddressCountryGridColumnShape(this.intl.locale),
      GridColumnField.correspondenceAddressCountry
    );
  }

  registeredAddressStreetAndNumber(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.REGISTERED_ADDRESS_STREET_AND_NUMBER,
      GridColumnField.registeredAddressStreetAndNumber
    );
  }

  registeredAddressPostalCode(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.REGISTERED_ADDRESS_POSTAL_CODE,
      GridColumnField.registeredAddressPostalCode
    );
  }

  registeredAddressCountry(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getRegisteredAddressCountryGridColumnShape(this.intl.locale),
      GridColumnField.registeredAddressCountry
    );
  }

  companyCreatedBy(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.COMPANY_CREATED_BY,
      GridColumnField.createdBy
    );
  }

  companyModifiedBy(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      COLUMN_SHAPES.COMPANY.COMPANY_MODIFIED_BY,
      GridColumnField.modifiedBy
    );
  }

  companySites(): GridColDef<T> {
    return this.mapShapeToColumnDef(
      getCompanySitesGridColumnShape(this.intl),
      GridColumnField.sites
    );
  }

  private mapShapeToColumnDef(
    shape: GridColumnShape,
    columnField: GridColumnField
  ): GridColDef {
    const {
      headerNameId,
      minWidth,
      width,
      resizable,
      renderCell,
      ...rest
    } = shape;

    const defaultRenderCell = renderCell
      ? renderCell
      : (params: GridRenderCellParams<T>) => {
          const value = params.formattedValue ?? params.value;

          if (isNil(value)) {
            return null;
          }

          return (
            <Typography variant='body2' sx={ELLIPSIS_TEXT_SX}>
              {String(value)}
            </Typography>
          );
        };

    return {
      ...rest,
      field: columnField,
      headerName: formatTranslation(this.intl, unsafeTranslationKey(headerNameId)),
      ...(width !== undefined && { width }),
      ...(width === undefined && { flex: 1 }),
      resizable: resizable ?? true,
      sortable: true,
      headerAlign: 'left',
      align: 'left',
      renderCell: defaultRenderCell,
    };
  }
}
